import React, { useEffect, useMemo } from 'react';
import './GdprPage.scss';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../LandingPage/LandingHeader';
import Footer from '../LandingPage/Footer';
import CookieConsent from '../LandingPage/CookieConsent';
import { trackPageView } from '../../analytics';
import { parseLegalContent } from '../../utils/legalContent';

const GDPR_CONTENT_EN = `## 3.1 Purpose of This Statement

This GDPR Compliance Statement supplements our Privacy Policy and describes, specifically, how FEEL IT SERVICES SAS (and its subsidiaries) complies with the EU General Data Protection Regulation (Regulation (EU) 2016/679, "GDPR") and equivalent data protection laws (such as the UK GDPR) when processing personal data of users of the Artefact App.

## 3.2 Data Controller

The data controller for the purposes of the GDPR is:

**FEEL IT SERVICES SAS (and its subsidiaries)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Phone: +33 6 62 88 36 50
Email: connect@feel-it-services.com

We have not appointed a Data Protection Officer (DPO), as this is not currently required given the nature and scale of our processing activities. All data protection inquiries and requests should be directed to the contact details above.

## 3.3 Categories of Personal Data Processed

- Identity and contact data: name, email address, account credentials.
- Images: photos captured of museum exhibits/artworks for scanning and recognition purposes.
- Location data: approximate or precise location, where permission is granted.
- Technical and usage data: device identifiers, app usage statistics, crash logs, IP address.

## 3.4 Purposes and Legal Bases

- Providing App functionality (account, scanning, itineraries) — Performance of a contract (Art. 6(1)(b) GDPR).
- Camera and location access for scanning and nearby recommendations — Consent (Art. 6(1)(a) GDPR).
- Analytics and App improvement — Legitimate interests (Art. 6(1)(f) GDPR).
- Security, fraud prevention, and legal compliance — Legitimate interests / Legal obligation (Art. 6(1)(c)/(f) GDPR).

## 3.5 Your Rights Under the GDPR

If you are located in the EEA, UK, or another jurisdiction with similar data protection laws, you have the following rights regarding your personal data:

- Right of access — obtain confirmation of, and access to, your personal data we process.
- Right to rectification — request correction of inaccurate or incomplete data.
- Right to erasure ("right to be forgotten") — request deletion of your data, subject to legal exceptions.
- Right to restriction of processing — request that we limit how we use your data in certain circumstances.
- Right to data portability — receive your data in a structured, machine-readable format, and transmit it to another controller.
- Right to object — object to processing based on legitimate interests or for direct marketing purposes.
- Right to withdraw consent — withdraw consent at any time, without affecting the lawfulness of processing carried out before withdrawal.
- Right not to be subject to solely automated decision-making producing legal or similarly significant effects (see Section 3.7 below).

## 3.6 How to Exercise Your Rights

To exercise any of the above rights, please contact us at the details below. We will respond to your request within one (1) month, as required by the GDPR, extendable by two further months for complex requests (we will inform you if this extension applies). We may need to verify your identity before processing your request.

**FEEL IT SERVICES SAS (and its subsidiaries)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Phone: +33 6 62 88 36 50
Email: connect@feel-it-services.com

## 3.7 Automated Decision-Making and AI Processing

The App uses AI-based image recognition to identify artworks and exhibits from photos you capture. This process:

- Analyzes only the visual content of the artwork/exhibit in the image, not personal data about you;
- Does not use facial recognition, biometric identification of individuals, or profiling of users;
- Does not produce legal or similarly significant effects concerning you, and therefore does not constitute automated decision-making within the meaning of Article 22 GDPR.

## 3.8 International Data Transfers

Where personal data is transferred outside the EEA/UK (for example, to hosting, analytics, or AI service providers located in other countries), we ensure such transfers are protected by appropriate safeguards recognized under the GDPR, including the European Commission's Standard Contractual Clauses (SCCs), adequacy decisions, or equivalent mechanisms.

Specifically, our processors (Google Analytics, Mixpanel, and OpenAI) are based in the United States. Data transferred to these processors is protected through the European Commission's Standard Contractual Clauses (SCCs) and, where applicable, additional contractual and technical safeguards.

## 3.9 Data Retention

We retain personal data only for as long as necessary for the purposes set out in this Statement and our Privacy Policy, in line with applicable legal requirements. Upon account deletion, personal data is deleted or anonymized within a reasonable period, except where retention is required by law.

## 3.10 Data Security Measures

We apply appropriate technical and organizational measures to protect personal data, including access controls, encryption of credentials, and secure transmission protocols (HTTPS/TLS), proportionate to the risks associated with the processing.

## 3.11 Right to Lodge a Complaint

If you believe your data protection rights have been violated, you have the right to lodge a complaint with a supervisory authority. In France, the competent authority is:

Commission Nationale de l'Informatique et des Libertés (CNIL)
3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France — www.cnil.fr

If you reside in another EEA country or the UK, you may also lodge a complaint with your local data protection supervisory authority.

## 3.12 Contact Us

For any questions regarding this GDPR Compliance Statement or to exercise your data protection rights, please contact:

**FEEL IT SERVICES SAS (and its subsidiaries)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Phone: +33 6 62 88 36 50
Email: connect@feel-it-services.com`;

const GDPR_CONTENT_RO = `## 3.1 Scopul acestei declarații

Această Declarație de Conformitate RGPD completează Politica noastră de Confidențialitate și descrie, în mod specific, modul în care FEEL IT SERVICES SAS (și filialele sale) respectă Regulamentul General privind Protecția Datelor al UE (Regulamentul (UE) 2016/679, "RGPD") și legislația echivalentă privind protecția datelor (precum UK GDPR) atunci când prelucrează datele cu caracter personal ale utilizatorilor Aplicației Artefact.

## 3.2 Operatorul de date

Operatorul de date în sensul RGPD este:

**FEEL IT SERVICES SAS (și filialele sale)**
13bis Avenue de la Motte Picquet, 75007 Paris, Franța
RCS Paris B 531 361 459
Telefon: +33 6 62 88 36 50
Email: connect@feel-it-services.com

Nu am desemnat un Responsabil cu Protecția Datelor (DPO), deoarece acest lucru nu este în prezent necesar, având în vedere natura și amploarea activităților noastre de prelucrare. Toate solicitările și întrebările privind protecția datelor trebuie adresate la datele de contact de mai sus.

## 3.3 Categorii de date cu caracter personal prelucrate

- Date de identitate și de contact: nume, adresă de email, credențiale de cont.
- Imagini: fotografii ale exponatelor/operelor de artă din muzee, capturate în scopul scanării și recunoașterii.
- Date de localizare: locație aproximativă sau precisă, acolo unde este acordată permisiunea.
- Date tehnice și de utilizare: identificatori ai dispozitivului, statistici de utilizare a aplicației, jurnale de erori, adresă IP.

## 3.4 Scopuri și temeiuri juridice

- Furnizarea funcționalităților Aplicației (cont, scanare, itinerarii) — Executarea unui contract (Art. 6 alin. (1) lit. (b) RGPD).
- Accesul la cameră și localizare pentru scanare și recomandări din apropiere — Consimțământ (Art. 6 alin. (1) lit. (a) RGPD).
- Analiză și îmbunătățirea Aplicației — Interese legitime (Art. 6 alin. (1) lit. (f) RGPD).
- Securitate, prevenirea fraudei și conformitate legală — Interese legitime / Obligație legală (Art. 6 alin. (1) lit. (c)/(f) RGPD).

## 3.5 Drepturile dumneavoastră conform RGPD

Dacă vă aflați în SEE, Regatul Unit sau o altă jurisdicție cu legi similare privind protecția datelor, aveți următoarele drepturi cu privire la datele dumneavoastră cu caracter personal:

- Dreptul de acces — de a obține confirmarea și accesul la datele dumneavoastră cu caracter personal pe care le prelucrăm.
- Dreptul la rectificare — de a solicita corectarea datelor inexacte sau incomplete.
- Dreptul la ștergere ("dreptul de a fi uitat") — de a solicita ștergerea datelor dumneavoastră, sub rezerva excepțiilor legale.
- Dreptul la restricționarea prelucrării — de a solicita limitarea modului în care utilizăm datele dumneavoastră în anumite circumstanțe.
- Dreptul la portabilitatea datelor — de a primi datele dumneavoastră într-un format structurat, care poate fi citit automat, și de a le transmite unui alt operator.
- Dreptul la opoziție — de a vă opune prelucrării bazate pe interese legitime sau efectuate în scopuri de marketing direct.
- Dreptul de a retrage consimțământul — de a retrage consimțământul în orice moment, fără a afecta legalitatea prelucrării efectuate înainte de retragere.
- Dreptul de a nu face obiectul unei decizii bazate exclusiv pe prelucrare automată, care produce efecte juridice sau vă afectează în mod similar semnificativ (a se vedea Secțiunea 3.7 de mai jos).

## 3.6 Cum vă puteți exercita drepturile

Pentru a vă exercita oricare dintre drepturile de mai sus, vă rugăm să ne contactați la datele de mai jos. Vă vom răspunde la solicitare în termen de una (1) lună, conform cerințelor RGPD, termen ce poate fi prelungit cu încă două luni pentru solicitări complexe (vă vom informa dacă se aplică această prelungire). Este posibil să fie necesar să vă verificăm identitatea înainte de a procesa solicitarea.

**FEEL IT SERVICES SAS (și filialele sale)**
13bis Avenue de la Motte Picquet, 75007 Paris, Franța
RCS Paris B 531 361 459
Telefon: +33 6 62 88 36 50
Email: connect@feel-it-services.com

## 3.7 Procesul decizional automatizat și prelucrarea prin IA

Aplicația utilizează recunoașterea imaginilor bazată pe IA pentru a identifica opere de artă și exponate din fotografiile pe care le capturați. Acest proces:

- Analizează doar conținutul vizual al operei de artă/exponatului din imagine, nu date cu caracter personal despre dumneavoastră;
- Nu utilizează recunoașterea facială, identificarea biometrică a persoanelor sau crearea de profiluri ale utilizatorilor;
- Nu produce efecte juridice sau alte efecte semnificative similare cu privire la dumneavoastră și, prin urmare, nu constituie un proces decizional automatizat în sensul Articolului 22 din RGPD.

## 3.8 Transferuri internaționale de date

Atunci când datele cu caracter personal sunt transferate în afara SEE/Regatului Unit (de exemplu, către furnizori de găzduire, analiză sau servicii de IA situați în alte țări), ne asigurăm că aceste transferuri sunt protejate prin garanții adecvate, recunoscute conform RGPD, inclusiv Clauzele Contractuale Standard (SCC) ale Comisiei Europene, deciziile de adecvare sau mecanisme echivalente.

Mai exact, persoanele împuternicite de noi (Google Analytics, Mixpanel și OpenAI) sunt situate în Statele Unite. Datele transferate către aceste persoane împuternicite sunt protejate prin Clauzele Contractuale Standard (SCC) ale Comisiei Europene și, acolo unde este cazul, prin garanții contractuale și tehnice suplimentare.

## 3.9 Păstrarea datelor

Păstrăm datele cu caracter personal doar atât timp cât este necesar pentru scopurile prevăzute în această Declarație și în Politica noastră de Confidențialitate, în conformitate cu cerințele legale aplicabile. La ștergerea contului, datele cu caracter personal sunt șterse sau anonimizate într-o perioadă rezonabilă, cu excepția cazului în care păstrarea este cerută de lege.

## 3.10 Măsuri de securitate a datelor

Aplicăm măsuri tehnice și organizatorice adecvate pentru a proteja datele cu caracter personal, inclusiv controale ale accesului, criptarea credențialelor și protocoale sigure de transmisie (HTTPS/TLS), proporționale cu riscurile asociate prelucrării.

## 3.11 Dreptul de a depune o plângere

Dacă considerați că drepturile dumneavoastră privind protecția datelor au fost încălcate, aveți dreptul de a depune o plângere la o autoritate de supraveghere. În Franța, autoritatea competentă este:

Commission Nationale de l'Informatique et des Libertés (CNIL)
3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France — www.cnil.fr

Dacă locuiți într-o altă țară din SEE sau în Regatul Unit, puteți depune, de asemenea, o plângere la autoritatea locală de supraveghere a protecției datelor.

## 3.12 Contactați-ne

Pentru orice întrebări legate de această Declarație de Conformitate RGPD sau pentru a vă exercita drepturile privind protecția datelor, vă rugăm să ne contactați:

**FEEL IT SERVICES SAS (și filialele sale)**
13bis Avenue de la Motte Picquet, 75007 Paris, Franța
RCS Paris B 531 361 459
Telefon: +33 6 62 88 36 50
Email: connect@feel-it-services.com`;

const GDPR_CONTENT_FR = `## 3.1 Objet de la présente déclaration

La présente Déclaration de conformité au RGPD complète notre Politique de confidentialité et décrit précisément comment FEEL IT SERVICES SAS (et ses filiales) se conforme au Règlement général sur la protection des données de l'UE (Règlement (UE) 2016/679, « RGPD ») et aux législations équivalentes en matière de protection des données (telles que le UK GDPR) lors du traitement des données personnelles des utilisateurs de l'Application Artefact.

## 3.2 Responsable du traitement

Le responsable du traitement au sens du RGPD est :

**FEEL IT SERVICES SAS (et ses filiales)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Téléphone : +33 6 62 88 36 50
E-mail : connect@feel-it-services.com

Nous n'avons pas désigné de Délégué à la protection des données (DPO), cette désignation n'étant pas requise à ce jour compte tenu de la nature et de l'ampleur de nos activités de traitement. Toutes les questions et demandes relatives à la protection des données doivent être adressées aux coordonnées ci-dessus.

## 3.3 Catégories de données personnelles traitées

- Données d'identité et de contact : nom, adresse e-mail, identifiants de compte.
- Images : photos d'objets exposés et d'œuvres d'art prises à des fins de scan et de reconnaissance.
- Données de localisation : localisation approximative ou précise, lorsque l'autorisation est accordée.
- Données techniques et d'utilisation : identifiants de l'appareil, statistiques d'utilisation de l'Application, journaux de plantage, adresse IP.

## 3.4 Finalités et bases juridiques

- Fourniture des fonctionnalités de l'Application (compte, scan, parcours) — Exécution d'un contrat (art. 6(1)(b) RGPD).
- Accès à l'appareil photo et à la localisation pour le scan et les recommandations à proximité — Consentement (art. 6(1)(a) RGPD).
- Analyse et amélioration de l'Application — Intérêts légitimes (art. 6(1)(f) RGPD).
- Sécurité, prévention de la fraude et conformité légale — Intérêts légitimes / Obligation légale (art. 6(1)(c)/(f) RGPD).

## 3.5 Vos droits au titre du RGPD

Si vous vous trouvez dans l'EEE, au Royaume-Uni ou dans une autre juridiction disposant de lois similaires en matière de protection des données, vous disposez des droits suivants concernant vos données personnelles :

- Droit d'accès — obtenir la confirmation que nous traitons vos données personnelles et y accéder.
- Droit de rectification — demander la correction de données inexactes ou incomplètes.
- Droit à l'effacement (« droit à l'oubli ») — demander la suppression de vos données, sous réserve des exceptions légales.
- Droit à la limitation du traitement — demander que nous limitions l'utilisation de vos données dans certaines circonstances.
- Droit à la portabilité des données — recevoir vos données dans un format structuré et lisible par machine, et les transmettre à un autre responsable du traitement.
- Droit d'opposition — vous opposer à un traitement fondé sur des intérêts légitimes ou effectué à des fins de prospection.
- Droit de retirer votre consentement — retirer votre consentement à tout moment, sans que cela n'affecte la licéité du traitement effectué avant ce retrait.
- Droit de ne pas faire l'objet d'une décision fondée exclusivement sur un traitement automatisé produisant des effets juridiques ou des effets similaires significatifs (voir la Section 3.7 ci-dessous).

## 3.6 Comment exercer vos droits

Pour exercer l'un des droits ci-dessus, veuillez nous contacter aux coordonnées ci-dessous. Nous répondrons à votre demande dans un délai d'un (1) mois, comme l'exige le RGPD, ce délai pouvant être prolongé de deux mois supplémentaires pour les demandes complexes (nous vous informerons le cas échéant). Il se peut que nous devions vérifier votre identité avant de traiter votre demande.

**FEEL IT SERVICES SAS (et ses filiales)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Téléphone : +33 6 62 88 36 50
E-mail : connect@feel-it-services.com

## 3.7 Prise de décision automatisée et traitement par IA

L'Application utilise une reconnaissance d'images par IA pour identifier les œuvres d'art et les objets exposés à partir des photos que vous prenez. Ce processus :

- Analyse uniquement le contenu visuel de l'œuvre ou de l'objet exposé figurant sur l'image, et non des données personnelles vous concernant ;
- N'utilise pas la reconnaissance faciale, l'identification biométrique de personnes ni le profilage des utilisateurs ;
- Ne produit pas d'effets juridiques ou d'effets similaires significatifs vous concernant et ne constitue donc pas une prise de décision automatisée au sens de l'article 22 du RGPD.

## 3.8 Transferts internationaux de données

Lorsque des données personnelles sont transférées en dehors de l'EEE ou du Royaume-Uni (par exemple, vers des prestataires d'hébergement, d'analyse ou d'IA situés dans d'autres pays), nous veillons à ce que ces transferts soient protégés par des garanties appropriées reconnues par le RGPD, notamment les clauses contractuelles types (CCT) de la Commission européenne, les décisions d'adéquation ou des mécanismes équivalents.

Plus précisément, nos sous-traitants (Google Analytics, Mixpanel et OpenAI) sont établis aux États-Unis. Les données transférées à ces sous-traitants sont protégées par les clauses contractuelles types (CCT) de la Commission européenne et, le cas échéant, par des garanties contractuelles et techniques supplémentaires.

## 3.9 Conservation des données

Nous ne conservons les données personnelles que pendant la durée nécessaire aux finalités énoncées dans la présente Déclaration et dans notre Politique de confidentialité, conformément aux exigences légales applicables. En cas de suppression du compte, les données personnelles sont supprimées ou anonymisées dans un délai raisonnable, sauf lorsque leur conservation est exigée par la loi.

## 3.10 Mesures de sécurité des données

Nous appliquons des mesures techniques et organisationnelles appropriées pour protéger les données personnelles, notamment des contrôles d'accès, le chiffrement des identifiants et des protocoles de transmission sécurisés (HTTPS/TLS), proportionnées aux risques liés au traitement.

## 3.11 Droit d'introduire une réclamation

Si vous estimez que vos droits en matière de protection des données ont été violés, vous avez le droit d'introduire une réclamation auprès d'une autorité de contrôle. En France, l'autorité compétente est :

Commission Nationale de l'Informatique et des Libertés (CNIL)
3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France — www.cnil.fr

Si vous résidez dans un autre pays de l'EEE ou au Royaume-Uni, vous pouvez également introduire une réclamation auprès de l'autorité de contrôle locale en matière de protection des données.

## 3.12 Nous contacter

Pour toute question concernant la présente Déclaration de conformité au RGPD ou pour exercer vos droits en matière de protection des données, veuillez contacter :

**FEEL IT SERVICES SAS (et ses filiales)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Téléphone : +33 6 62 88 36 50
E-mail : connect@feel-it-services.com`;

const GDPR_CONTENT_RU = `## 3.1 Цель настоящего заявления

Настоящее Заявление о соответствии GDPR дополняет нашу Политику конфиденциальности и подробно описывает, как FEEL IT SERVICES SAS (и её дочерние компании) соблюдает Общий регламент ЕС по защите данных (Регламент (ЕС) 2016/679, «GDPR») и аналогичное законодательство о защите данных (например, UK GDPR) при обработке персональных данных пользователей Приложения Artefact.

## 3.2 Оператор данных

Оператором данных в целях GDPR является:

**FEEL IT SERVICES SAS (и её дочерние компании)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Телефон: +33 6 62 88 36 50
Эл. почта: connect@feel-it-services.com

Мы не назначали Инспектора по защите данных (DPO), поскольку в настоящее время это не требуется с учётом характера и масштаба нашей деятельности по обработке данных. Все вопросы и запросы, связанные с защитой данных, следует направлять по указанным выше контактным данным.

## 3.3 Категории обрабатываемых персональных данных

- Идентификационные и контактные данные: имя, адрес электронной почты, учётные данные.
- Изображения: фотографии музейных экспонатов и произведений искусства, сделанные для сканирования и распознавания.
- Данные о местоположении: приблизительное или точное местоположение, если дано разрешение.
- Технические данные и данные об использовании: идентификаторы устройства, статистика использования Приложения, журналы сбоев, IP-адрес.

## 3.4 Цели и правовые основания

- Предоставление функций Приложения (учётная запись, сканирование, маршруты) — Исполнение договора (ст. 6(1)(b) GDPR).
- Доступ к камере и местоположению для сканирования и рекомендаций поблизости — Согласие (ст. 6(1)(a) GDPR).
- Аналитика и улучшение Приложения — Законные интересы (ст. 6(1)(f) GDPR).
- Безопасность, предотвращение мошенничества и соблюдение законодательства — Законные интересы / Юридическое обязательство (ст. 6(1)(c)/(f) GDPR).

## 3.5 Ваши права в соответствии с GDPR

Если вы находитесь в ЕЭЗ, Великобритании или иной юрисдикции с аналогичным законодательством о защите данных, вы обладаете следующими правами в отношении своих персональных данных:

- Право на доступ — получить подтверждение того, что мы обрабатываем ваши персональные данные, и доступ к ним.
- Право на исправление — потребовать исправления неточных или неполных данных.
- Право на удаление («право на забвение») — потребовать удаления ваших данных с учётом исключений, предусмотренных законом.
- Право на ограничение обработки — потребовать, чтобы мы ограничили использование ваших данных в определённых обстоятельствах.
- Право на переносимость данных — получить свои данные в структурированном, машиночитаемом формате и передать их другому оператору.
- Право на возражение — возразить против обработки, основанной на законных интересах или осуществляемой в целях прямого маркетинга.
- Право отозвать согласие — отозвать согласие в любое время без ущерба для законности обработки, осуществлённой до его отзыва.
- Право не подвергаться решению, основанному исключительно на автоматизированной обработке и влекущему юридические или аналогичные значимые последствия (см. Раздел 3.7 ниже).

## 3.6 Как реализовать свои права

Чтобы воспользоваться любым из перечисленных выше прав, свяжитесь с нами по указанным ниже контактным данным. Мы ответим на ваш запрос в течение одного (1) месяца, как того требует GDPR; для сложных запросов этот срок может быть продлён ещё на два месяца (в этом случае мы вас уведомим). Перед обработкой запроса нам может потребоваться подтвердить вашу личность.

**FEEL IT SERVICES SAS (и её дочерние компании)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Телефон: +33 6 62 88 36 50
Эл. почта: connect@feel-it-services.com

## 3.7 Автоматизированное принятие решений и обработка с помощью ИИ

Приложение использует распознавание изображений на основе ИИ для идентификации произведений искусства и экспонатов на сделанных вами фотографиях. Этот процесс:

- Анализирует только визуальное содержание произведения или экспоната на изображении, а не ваши персональные данные;
- Не использует распознавание лиц, биометрическую идентификацию людей или профилирование пользователей;
- Не влечёт для вас юридических или аналогичных значимых последствий и поэтому не является автоматизированным принятием решений в смысле статьи 22 GDPR.

## 3.8 Международная передача данных

Если персональные данные передаются за пределы ЕЭЗ/Великобритании (например, поставщикам услуг хостинга, аналитики или ИИ, расположенным в других странах), мы обеспечиваем защиту такой передачи надлежащими гарантиями, признанными GDPR, включая Стандартные договорные положения (SCC) Европейской комиссии, решения о достаточности уровня защиты или эквивалентные механизмы.

В частности, наши обработчики (Google Analytics, Mixpanel и OpenAI) находятся в США. Данные, передаваемые этим обработчикам, защищены Стандартными договорными положениями (SCC) Европейской комиссии и, где применимо, дополнительными договорными и техническими гарантиями.

## 3.9 Хранение данных

Мы храним персональные данные только в течение срока, необходимого для целей, изложенных в настоящем Заявлении и в нашей Политике конфиденциальности, в соответствии с применимыми требованиями законодательства. После удаления учётной записи персональные данные удаляются или обезличиваются в разумный срок, за исключением случаев, когда их хранение требуется по закону.

## 3.10 Меры по обеспечению безопасности данных

Мы применяем надлежащие технические и организационные меры для защиты персональных данных, включая контроль доступа, шифрование учётных данных и защищённые протоколы передачи данных (HTTPS/TLS), соразмерные рискам, связанным с обработкой.

## 3.11 Право на подачу жалобы

Если вы считаете, что ваши права на защиту данных были нарушены, вы имеете право подать жалобу в надзорный орган. Во Франции компетентным органом является:

Commission Nationale de l'Informatique et des Libertés (CNIL)
3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07, France — www.cnil.fr

Если вы проживаете в другой стране ЕЭЗ или в Великобритании, вы также можете подать жалобу в местный надзорный орган по защите данных.

## 3.12 Свяжитесь с нами

По любым вопросам, касающимся настоящего Заявления о соответствии GDPR, или для реализации своих прав на защиту данных, пожалуйста, обращайтесь:

**FEEL IT SERVICES SAS (и её дочерние компании)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Телефон: +33 6 62 88 36 50
Эл. почта: connect@feel-it-services.com`;

const GDPR_BLOCKS_EN = parseLegalContent(GDPR_CONTENT_EN);
const GDPR_BLOCKS_RO = parseLegalContent(GDPR_CONTENT_RO);
const GDPR_BLOCKS_FR = parseLegalContent(GDPR_CONTENT_FR);
const GDPR_BLOCKS_RU = parseLegalContent(GDPR_CONTENT_RU);

const GDPR_BLOCKS_BY_LANGUAGE = {
  ro: GDPR_BLOCKS_RO,
  fr: GDPR_BLOCKS_FR,
  ru: GDPR_BLOCKS_RU,
};

const GdprPage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('GDPR Page');
  }, []);

  const blocks = useMemo(
    () => GDPR_BLOCKS_BY_LANGUAGE[i18n.resolvedLanguage] || GDPR_BLOCKS_EN,
    [i18n.resolvedLanguage]
  );

  return (
    <div className="gdpr-page">
      <LandingHeader />

      <main className="gdpr-page__main">
        <span className="gdpr-page__eyebrow">
          {t('landing.gdprPage.eyebrow', 'Legal')}
        </span>
        <h1 className="gdpr-page__title font-display">
          {t('landing.gdprPage.title', 'GDPR Compliance Statement')}
        </h1>
        <p className="gdpr-page__updated">
          {t('landing.gdprPage.lastUpdated', 'Last updated: August 21, 2026')}
        </p>

        <div className="gdpr-page__content">
          {blocks.map((block, index) => {
            if (block.type === 'heading') {
              return (
                <h2 key={index} className="gdpr-page__heading">
                  {block.text}
                </h2>
              );
            }

            if (block.type === 'subheading') {
              return (
                <h3 key={index} className="gdpr-page__subheading">
                  {block.text}
                </h3>
              );
            }

            if (block.type === 'list') {
              return (
                <ul key={index} className="gdpr-page__list">
                  {block.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      {item.label ? `${item.label}: ${item.text}` : item.text}
                    </li>
                  ))}
                </ul>
              );
            }

            return (
              <p key={index} className="gdpr-page__paragraph">
                {block.bold ? <strong>{block.text}</strong> : block.text}
              </p>
            );
          })}
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default GdprPage;
